import { useEffect, useState } from "react";
import { PageHeader } from "../App";
import { 
  Copy, 
  Trash, 
  MagnifyingGlass,
  CheckCircle,
  Spinner,
  ArrowsClockwise,
  Scissors,
  ChatCircleDots
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import api from "../lib/api";

const DuplicateGroup = ({ group, index }) => {
  return (
    <div className="border border-zinc-800 rounded-md overflow-hidden">
      <div className="bg-zinc-800/50 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Copy weight="duotone" className="w-5 h-5 text-amber-400" />
          <span className="text-sm text-zinc-300">
            Duplicate Group #{index + 1}
          </span>
          <span className="badge badge-yellow">
            {group.count} copies
          </span>
        </div>
        <span className="text-xs font-mono text-zinc-500">
          hash: {group.content_hash}
        </span>
      </div>
      
      <div className="divide-y divide-zinc-800/50">
        {group.conversations.map((conv, idx) => (
          <div key={conv.id} className="px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {idx === 0 ? (
                <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400" />
              ) : (
                <Trash weight="regular" className="w-4 h-4 text-zinc-500" />
              )}
              <div>
                <p className="text-sm text-zinc-200">{conv.title || 'Untitled'}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs text-zinc-500">{conv.message_count} messages</span>
                  <span className={`badge badge-${
                    conv.source_platform === 'chatgpt' ? 'green' :
                    conv.source_platform === 'perplexity' ? 'blue' : 'yellow'
                  }`}>
                    {conv.source_platform}
                  </span>
                </div>
              </div>
            </div>
            {idx === 0 && (
              <span className="text-xs text-emerald-400">Will keep</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default function Processing() {
  const [duplicates, setDuplicates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [scanned, setScanned] = useState(false);
  
  // Splitting state
  const [datasets, setDatasets] = useState([]);
  const [selectedDataset, setSelectedDataset] = useState("all");
  const [minMessages, setMinMessages] = useState(15);
  const [maxChunkSize, setMaxChunkSize] = useState(10);
  const [splitting, setSplitting] = useState(false);
  const [splitResults, setSplitResults] = useState(null);

  useEffect(() => {
    loadDatasets();
  }, []);

  const loadDatasets = async () => {
    try {
      const data = await api.getDatasets();
      setDatasets(data);
    } catch (error) {
      console.error("Failed to load datasets:", error);
    }
  };

  const findDuplicates = async () => {
    setLoading(true);
    try {
      const data = await api.findDuplicates();
      setDuplicates(data.duplicates || []);
      setScanned(true);
      
      if (data.duplicates.length === 0) {
        toast.success("No duplicates found!");
      } else {
        toast.info(`Found ${data.duplicate_groups} duplicate groups`);
      }
    } catch (error) {
      console.error("Failed to find duplicates:", error);
      toast.error("Failed to scan for duplicates");
    } finally {
      setLoading(false);
    }
  };

  const removeDuplicates = async () => {
    setRemoving(true);
    try {
      const result = await api.removeDuplicates();
      toast.success(`Removed ${result.removed} duplicate conversations`);
      setDuplicates([]);
      setScanned(false);
    } catch (error) {
      console.error("Failed to remove duplicates:", error);
      toast.error("Failed to remove duplicates");
    } finally {
      setRemoving(false);
    }
  };

  const handleBatchSplit = async () => {
    setSplitting(true);
    setSplitResults(null);
    try {
      const datasetId = selectedDataset === "all" ? undefined : selectedDataset;
      const config = { max_messages: maxChunkSize, overlap: 2, min_messages: 4 };
      const result = await api.batchSplitConversations(datasetId, minMessages, config);
      setSplitResults(result);
      
      const chunksCreated = result.results.reduce((sum, r) => sum + (r.chunks_created || 0), 0);
      if (chunksCreated > 0) {
        toast.success(`Split ${result.processed} conversations into ${chunksCreated} chunks`);
      } else {
        toast.info("No conversations needed splitting");
      }
    } catch (error) {
      console.error("Failed to split conversations:", error);
      toast.error("Failed to split conversations");
    } finally {
      setSplitting(false);
    }
  };

  const totalDuplicates = duplicates.reduce((sum, g) => sum + g.count - 1, 0);

  return (
    <div>
      <PageHeader 
        title="Data Processing" 
        subtitle="Deduplicate and clean your datasets"
      />
      
      <div className="p-8 space-y-6">
        {/* Deduplication Card */}
        <div className="chart-container">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-medium text-zinc-100">Deduplication</h3>
              <p className="text-sm text-zinc-500 mt-1">
                Find and remove duplicate conversations across your datasets
              </p>
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={findDuplicates}
                disabled={loading}
                data-testid="scan-duplicates-btn"
              >
                {loading ? (
                  <>
                    <Spinner weight="bold" className="w-4 h-4 mr-2 animate-spin" />
                    Scanning...
                  </>
                ) : (
                  <>
                    <MagnifyingGlass weight="bold" className="w-4 h-4 mr-2" />
                    Scan for Duplicates
                  </>
                )}
              </Button>
              
              {duplicates.length > 0 && (
                <Button
                  variant="destructive"
                  onClick={removeDuplicates}
                  disabled={removing}
                  data-testid="remove-duplicates-btn"
                >
                  {removing ? (
                    <>
                      <Spinner weight="bold" className="w-4 h-4 mr-2 animate-spin" />
                      Removing...
                    </>
                  ) : (
                    <>
                      <Trash weight="bold" className="w-4 h-4 mr-2" />
                      Remove {totalDuplicates} Duplicates
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>

          {/* Stats */}
          {scanned && (
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="p-4 bg-zinc-900/50 rounded-md">
                <p className="text-xs text-zinc-500 uppercase tracking-wider">Duplicate Groups</p>
                <p className="text-2xl font-mono text-zinc-100 mt-1">{duplicates.length}</p>
              </div>
              <div className="p-4 bg-zinc-900/50 rounded-md">
                <p className="text-xs text-zinc-500 uppercase tracking-wider">Total Duplicates</p>
                <p className="text-2xl font-mono text-amber-400 mt-1">{totalDuplicates}</p>
              </div>
              <div className="p-4 bg-zinc-900/50 rounded-md">
                <p className="text-xs text-zinc-500 uppercase tracking-wider">To Keep</p>
                <p className="text-2xl font-mono text-emerald-400 mt-1">{duplicates.length}</p>
              </div>
            </div>
          )}

          {/* Duplicate Groups */}
          {duplicates.length > 0 ? (
            <div className="space-y-4">
              {duplicates.map((group, idx) => (
                <DuplicateGroup key={group.content_hash} group={group} index={idx} />
              ))}
            </div>
          ) : scanned ? (
            <div className="py-12 text-center">
              <CheckCircle weight="thin" className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
              <p className="text-zinc-300">No duplicates found</p>
              <p className="text-sm text-zinc-500 mt-1">Your dataset is clean!</p>
            </div>
          ) : (
            <div className="py-12 text-center">
              <ArrowsClockwise weight="thin" className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
              <p className="text-zinc-500">Click "Scan for Duplicates" to start</p>
              <p className="text-sm text-zinc-600 mt-1">
                Duplicates are identified by content hash
              </p>
            </div>
          )}
        </div>

        {/* Processing Info */}
        <div className="chart-container">
          <h3 className="text-sm font-medium text-zinc-400 mb-4 uppercase tracking-wider">
            How It Works
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-zinc-900/30 rounded-md">
              <div className="w-8 h-8 rounded bg-blue-500/10 flex items-center justify-center mb-3">
                <span className="text-blue-400 font-mono text-sm">1</span>
              </div>
              <p className="text-sm text-zinc-200">Content Hashing</p>
              <p className="text-xs text-zinc-500 mt-1">
                Each conversation is hashed based on its message content
              </p>
            </div>
            <div className="p-4 bg-zinc-900/30 rounded-md">
              <div className="w-8 h-8 rounded bg-amber-500/10 flex items-center justify-center mb-3">
                <span className="text-amber-400 font-mono text-sm">2</span>
              </div>
              <p className="text-sm text-zinc-200">Group Detection</p>
              <p className="text-xs text-zinc-500 mt-1">
                Conversations with identical hashes are grouped together
              </p>
            </div>
            <div className="p-4 bg-zinc-900/30 rounded-md">
              <div className="w-8 h-8 rounded bg-emerald-500/10 flex items-center justify-center mb-3">
                <span className="text-emerald-400 font-mono text-sm">3</span>
              </div>
              <p className="text-sm text-zinc-200">Smart Removal</p>
              <p className="text-xs text-zinc-500 mt-1">
                First conversation in each group is kept, duplicates removed
              </p>
            </div>
          </div>
        </div>

        {/* Conversation Splitting */}
        <div className="chart-container">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-medium text-zinc-100">Conversation Splitting</h3>
              <p className="text-sm text-zinc-500 mt-1">
                Break long conversations into smaller training chunks
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="space-y-2">
              <Label className="text-zinc-400">Dataset</Label>
              <Select value={selectedDataset} onValueChange={setSelectedDataset}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800">
                  <SelectValue placeholder="All datasets" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Datasets</SelectItem>
                  {datasets.map(d => (
                    <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-400">Min Messages to Split</Label>
              <Input
                type="number"
                min="5"
                value={minMessages}
                onChange={(e) => setMinMessages(parseInt(e.target.value) || 15)}
                className="bg-zinc-900 border-zinc-800"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-400">Max Chunk Size</Label>
              <Input
                type="number"
                min="4"
                max="50"
                value={maxChunkSize}
                onChange={(e) => setMaxChunkSize(parseInt(e.target.value) || 10)}
                className="bg-zinc-900 border-zinc-800"
              />
            </div>
            
            <div className="flex items-end">
              <Button
                onClick={handleBatchSplit}
                disabled={splitting}
                className="w-full"
                data-testid="split-conversations-btn"
              >
                {splitting ? (
                  <>
                    <Spinner weight="bold" className="w-4 h-4 mr-2 animate-spin" />
                    Splitting...
                  </>
                ) : (
                  <>
                    <Scissors weight="bold" className="w-4 h-4 mr-2" />
                    Split Conversations
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Split Results */}
          {splitResults && (
            <div className="border border-zinc-800 rounded-md">
              <div className="bg-zinc-800/50 px-4 py-3 flex items-center justify-between">
                <span className="text-sm text-zinc-300">
                  Processed {splitResults.processed} conversation(s)
                </span>
              </div>
              <div className="max-h-[300px] overflow-y-auto">
                {splitResults.results.map((result, idx) => (
                  <div key={idx} className="px-4 py-3 border-b border-zinc-800/50 last:border-0 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <ChatCircleDots weight="regular" className="w-4 h-4 text-zinc-500" />
                      <span className="text-sm text-zinc-400 font-mono">{result.id?.slice(0, 8)}...</span>
                    </div>
                    {result.chunks_created ? (
                      <span className="text-xs text-emerald-400">
                        {result.original_messages} msgs → {result.chunks_created} chunks
                      </span>
                    ) : result.error ? (
                      <span className="text-xs text-red-400">{result.error}</span>
                    ) : (
                      <span className="text-xs text-zinc-500">Already small enough</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {!splitResults && (
            <div className="py-8 text-center">
              <Scissors weight="thin" className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-500 text-sm">Configure settings and click "Split Conversations"</p>
              <p className="text-zinc-600 text-xs mt-1">
                Long conversations will be split with overlapping context
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
